import { z } from 'zod'
import { RoleIdSchema, type RoleId } from '../role'
import {
  ReportDateRangeSchema,
  ReportKpiGroupingManagerSchema,
  ReportKpiFilterSchema,
  ReportKpiGroupingTeamSchema,
  ReportKpiGroupingUserSchema,
  ReportKpiTypeSchema,
} from '../report'
import { ReportFormattersSchema } from '../ratio'
import { CompanyIdSchema, EntityIdSchema, idSchemaV2, isId, type EntityId } from '../ids'
import { AuditPropsSchema } from '../common'

export const DashboardWidgetIdSchema = idSchemaV2('DashWidget')
export type DashboardWidgetId = z.infer<typeof DashboardWidgetIdSchema>
export const isDashboardWidgetId = isId(DashboardWidgetIdSchema)

export type DashboardWidgetType = DashboardWidget['type']

export const DashboardWidgetMetricSchema = z.object({
  kpi: ReportKpiTypeSchema,
  threshold: z.number().optional(),
  gauge: z.boolean().optional(),
  format: ReportFormattersSchema.optional(),
})
export type DashboardWidgetMetric = z.infer<typeof DashboardWidgetMetricSchema>

export const DashboardWidgetResolutionSchema = z.enum(['auto', 'hour', 'day', 'week', 'month', 'quarter', 'year'])
export type DashboardWidgetResolution = z.infer<typeof DashboardWidgetResolutionSchema>

export const DashboardWidgetDirectionSchema = z.enum(['asc', 'desc'])
export type DashboardWidgetDirection = z.infer<typeof DashboardWidgetDirectionSchema>

export const DashboardWidgetGroupingSchema = z.discriminatedUnion('type', [
  ReportKpiGroupingUserSchema,
  ReportKpiGroupingTeamSchema,
  ReportKpiGroupingManagerSchema,
])
export type DashboardWidgetGrouping = z.infer<typeof DashboardWidgetGroupingSchema>

export const DashboardCurrentUserFilterSchema = z.object({
  type: z.literal('current_user'),
})
export type DashboardCurrentUserFilter = z.infer<typeof DashboardCurrentUserFilterSchema>

export const DashboardCurrentTeamFilterSchema = z.object({
  type: z.literal('current_team'),
})
export type DashboardCurrentTeamFilter = z.infer<typeof DashboardCurrentTeamFilterSchema>

export const DashboardFixedFilterSchema = z.object({
  type: z.literal('fixed'),
  filter: ReportKpiFilterSchema.pick({ teams: true, users: true, roles: true }),
})
export type DashboardFixedFilter = z.infer<typeof DashboardFixedFilterSchema>

export const DashboardFilterSchema = z.discriminatedUnion('type', [
  DashboardCurrentUserFilterSchema,
  DashboardCurrentTeamFilterSchema,
  DashboardFixedFilterSchema,
])
export type DashboardFilter = z.infer<typeof DashboardFilterSchema>

const DashboardWidgetCommonSchema = z.object({
  widgetId: DashboardWidgetIdSchema,
  name: z.string().optional(),
  dateRange: ReportDateRangeSchema.optional(),
  filter: DashboardFilterSchema.optional(),
})

export const DashboardWidgetBaseSchema = z.object({
  ...DashboardWidgetCommonSchema.shape,
  metrics: DashboardWidgetMetricSchema.array(),
  resolution: DashboardWidgetResolutionSchema.optional(),
})

export const DashboardNumberWidget = z.object({
  ...DashboardWidgetBaseSchema.shape,
  type: z.literal('number'),
})

export const DashboardLeaderboardWidget = z.object({
  ...DashboardWidgetBaseSchema.shape,
  type: z.literal('leaderboard'),
  direction: DashboardWidgetDirectionSchema.optional(),
  grouping: DashboardWidgetGroupingSchema.optional(),
})

export const DashboardLineWidget = z.object({
  ...DashboardWidgetBaseSchema.shape,
  type: z.literal('line'),
  threshold: z.number().optional(),
  thresholdColor: z.string().optional(),
})

export const DashboardTableWidget = z.object({
  ...DashboardWidgetBaseSchema.shape,
  type: z.literal('table'),
  grouping: DashboardWidgetGroupingSchema.optional(),
})

export const DashboardGoalWidget = z.object({
  ...DashboardWidgetCommonSchema.shape,
  type: z.literal('goal'),
  metrics: z.never().array().default([]),
})

export const DashboardWidgetSchema = z.discriminatedUnion('type', [
  DashboardNumberWidget,
  DashboardGoalWidget,
  DashboardLeaderboardWidget,
  DashboardLineWidget,
  DashboardTableWidget,
])
export type DashboardWidget = z.infer<typeof DashboardWidgetSchema>

export const DashboardPlatformSchema = z.enum(['web', 'mobile'])
export type DashboardPlatform = z.infer<typeof DashboardPlatformSchema>

export type DashboardScopeId = EntityId | RoleId

export const DASHBOARD_ORDER_GAP = 1_000_000_000

export const UnsavedDashboardDataSchema = z.object({
  name: z.string().optional(),
  dateRange: ReportDateRangeSchema.optional(),
  widgets: DashboardWidgetSchema.array(),
  platform: DashboardPlatformSchema.array().optional(),
  order: z.number().optional(),
  scopeId: EntityIdSchema.or(RoleIdSchema).optional(),
  filter: DashboardFilterSchema.optional(),
  home: z.boolean().optional(),
})

export const DashboardIdSchema = idSchemaV2('Dashboard')
export type DashboardId = z.infer<typeof DashboardIdSchema>
export const isDashboardId = isId(DashboardIdSchema)

export const DashboardDataSchema = z
  .object({
    ...AuditPropsSchema.shape,
    dashboardId: DashboardIdSchema,
    companyId: CompanyIdSchema,
    scopeId: EntityIdSchema.or(RoleIdSchema),
    order: z.number(),
  })
  .and(UnsavedDashboardDataSchema.omit({ scopeId: true, order: true }))
export type DashboardData = z.infer<typeof DashboardDataSchema>

export const DashboardDataWithEditableSchema = DashboardDataSchema.and(
  z.object({
    editable: z
      .boolean()
      .describe('Whether the caller has `dashboard:manage` on this dashboard, so can update or remove it'),
  })
)
export type DashboardDataWithEditable = z.infer<typeof DashboardDataWithEditableSchema>
